// @flow
// TODO: rename file to api-calls.js for filenaming consistency
import Vue from 'vue'
import store from 'app/store'
import * as errorTracker from 'app/error-tracker'

/**
 *
 * make get request with Config with Vue Resource to a FB API URL
 * handles generic errors with modals for logged out and 500 errors
 *
 * @param url string - after the api/v1/
 * @param config Object pass to Vue Resource
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIGetWithConfig = (
  url: string,
  config: Object,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
) => {
  fbAPICall('get', url, config, successCallback, errorCallback)
}

/**
 *
 * make get request with Vue Resource to a FB API URL
 * handle generic errors with modals for logged out and 500 errors
 *
 * @param url string - after the api/v1/
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIGet = (url: string, successCallback: Function, errorCallback: Function) => {
  fbAPICall('get', url, [], successCallback, errorCallback)
}

/**
 * make post request with Vue Resource to a FB API URL
 * handle generic errors with modals for logged out and 500 errors
 *
 * @param url - after the api/v1/
 * @param data - usually a window.FormData() object
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIPost = (
  url: string,
  data?: any,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
) => {
  fbAPICall('post', url, data, successCallback, errorCallback)
}

/**
 * make post request with Vue Resource to a FB API URL
 * handle generic errors with modals for logged out and 500 errors
 *
 * @param url - after the api/v1/
 * @param data - usually a window.FormData() object
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIPostMulti = (
  url: string,
  data: any,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
) => {
  fbAPICall('postMulti', url, data, successCallback, errorCallback)
}

/**
 * make post request with Vue Resource to a FB API URL
 * handle "expected" errors in successCallback
 * handle generic errors with modals for logged out and 500 errors
 *
 * @param url - after the api/v1/
 * @param data - usually a window.FormData() object
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIPostHandleExpectedErrors = (
  url: string,
  data: any,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
) => {
  fbAPICall('post', url, data, successCallback, errorCallback, true)
}

/**
 *
 * make delete request with Vue Resource to a FB API URL
 * handle generic errors with modals for logged out and 500 errors
 *
 * @param url string - after the api/v1/
 * @param successCallback
 * @param errorCallback
 */
export const fbAPIDelete = (
  url: string,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
) => {
  fbAPICall('delete', url, [], successCallback, errorCallback)
}

/**
 * make actual Vue Resource call to a FB API URL
 * TODO: migrate everything new fbApiPromise and remove this
 * TODO: when migrating, hasValidationErrors = skipErrorModal
 *
 * @param callType string - get or post
 * @param url string - url to call after /api/v1
 * @param configOrBody - for get this is config, for post this is body
 * @param successCallback
 * @param errorCallback
 * @param hasValidationErrors boolean
 */
export const fbAPICall = (
  callType: string,
  url: string,
  configOrBody: Object,
  successCallback?: (res: any) => void,
  errorCallback?: (err: any) => void,
  hasValidationErrors?: boolean = false,
) => {
  // if hasValidationErrors then could be invalid data that should be shown to user
  // in which case the successCallback should handle.
  // otherwise any error is unexpected so just put up the ErrorModal
  const successFunction = (res) => {
    if (!hasValidationErrors && res.body.status === 'error') {
      // if error just show generic error modal and don't process data
      store.dispatch('showErrorModal')
      if (typeof errorCallback !== 'undefined') {
        errorCallback(res)
      }
      return
    }
    if (typeof successCallback !== 'undefined') {
      successCallback(res)
    }
  }

  const errFunction = (err) => {
    if (err.status === 401) {
      store.dispatch('showWasLoggedInModal')
    } else {
      store.dispatch('showErrorModal')
    }

    if (typeof errorCallback !== 'undefined') {
      errorCallback(err)
    }
  }

  if (callType === 'get') {
    Vue.http.get(`/api/v1/` + url, configOrBody).then(successFunction, errFunction)
  } else if (callType === 'post') {
    Vue.http.post(`/api/v1/` + url, configOrBody).then(successFunction, errFunction)
  } else if (callType === 'postMulti') {
    const multipartHeader = { headers: { 'Content-Type': 'multipart/form-data' } }
    Vue.http
      .post(`/api/v1/` + url, configOrBody, multipartHeader)
      .then(successFunction, errFunction)
  } else if (callType === 'put') {
    Vue.http.put(`/api/v1/` + url, configOrBody).then(successFunction, errFunction)
  } else if (callType === 'delete') {
    Vue.http.delete(`/api/v1/` + url, configOrBody).then(successFunction, errFunction)
  }
}

/**
 * Promisified version of fbApiCall above, uses object destructuring to avoid
 * param order issues
 */
export const fbAPIPromise = async (
  { callType, url, body, config, skipErrorModal, prefix }: FbApiParams = {
    callType: 'get',
    url: '',
    body: null,
    config: null,
    headers: {},
    // allows skipping error modals (200 + status = error, 4xx and 5xx)
    skipErrorModal: false,
    prefix: 'api', // api|fbadmin
  },
): Promise<VueResourceResponse> => {
  // default to public prefix if not set
  const endpointPrefix = prefix === 'fbadmin' ? '/fbadmin/api/v1' : '/api/v1'
  const endpoint = `${endpointPrefix}${url}`

  // multipart headers (inline type to stope flow errors)
  const configDefaults: {
    headers?: any,
  } = {
    ...config,
  }

  // add multipart headers
  if (callType === 'postMulti') {
    if (configDefaults.headers) {
      configDefaults.headers['Content-Type'] = 'multipart/form-data'
    } else {
      configDefaults.headers = { 'Content-Type': 'multipart/form-data' }
    }
  }

  // simple call types
  let httpResource

  if (['get', 'delete'].includes(callType)) {
    // simple calls, no body
    httpResource = Vue.http[callType](endpoint, configDefaults)
  } else if (['put', 'patch', 'post'].includes(callType)) {
    // include body
    httpResource = Vue.http[callType](endpoint, body, configDefaults)
  } else {
    // default if something crazy was passed in, do a get
    httpResource = Vue.http.get(endpoint, configDefaults)
  }

  // should have a valid http promise at this point
  return new Promise((resolve, reject) => {
    httpResource.then(
      // success
      (res: VueResourceResponse) => {
        // could be invalid data that should be shown to user
        // in which case the successCallback should handle.
        // otherwise any error is unexpected so just put up the ErrorModal
        if (!skipErrorModal && res.body.status === 'error') {
          // if error just show generic error modal and don't process data
          store.dispatch('showErrorModal')

          // send to front end error tracker
          errorTracker.captureMessage({
            msg: `Vue HTTP API Error`,
            extras: {
              url: res.url,
              statusCode: res.status,
              statusText: res.statusText,
              message: res.body && res.body.message ? res.body.message : '',
            },
          })

          return reject(res)
        }

        return resolve(res)
      },

      // error
      (res: VueResourceResponse) => {
        if (res.status === 401) {
          store.dispatch('showWasLoggedInModal')
          // no error tracker for auth errors
        } else {
          // other 4xx or 5xx errors
          if (!skipErrorModal) {
            store.dispatch('showErrorModal')
          }

          // send to front end error tracker
          errorTracker.captureMessage({
            msg: `Vue HTTP API Error`,
            extras: {
              url: res.url,
              statusCode: res.status,
              statusText: res.statusText,
              message: res.body && res.body.message ? res.body.message : '',
            },
          })
        }

        return reject(res)
      },
    )
  })
}
